
import React, { useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setActiveMenuValue } from "../../Slices/taskSlice";
import { openTab } from "../../Slices/tabSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUserTie,
  faChartLine,
  faTasks,
  faCog,
  faChartBar,
  faEnvelope,
  faDatabase,
  faTachometerAlt,
  faMailBulk,
  faWallet,
  faFolder,
  faChartSimple,
  faFileInvoiceDollar,
  faRoute,
  faExclamationTriangle,
  faPhone,
  faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import { SIDEMENU } from "../../databucket";
import { fetchHandbooksForEmployee } from "../../Slices/handBookSlice";
const Sidebar = (props) => {

  const dispatch = useDispatch();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [openInnerSubMenu, setOpenInnerSubMenu] = useState(null);
  const location = useLocation();
  const pathname = location.pathname;
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const userId = loginUser?.id;
  const CurrentUserAccess = useSelector((state) => state?.employee?.currentEmployeeAccess);
  const handbooks = useSelector((state) => state?.handbooks?.myHandbooks || []);

  const CurrentUserRoute = CurrentUserAccess?.routes || [];

  // 1️⃣ Move these to the top of the file, after imports

  function unformatName(name) {
    return name.replace(/ /g, '_');
  }
// Routes accessible to all logged-in users without access management
const ALWAYS_VISIBLE_ROUTES = ["/MyDashboard"];

const filterSideMenuByRoutes = (SIDEMENU, CurrentUserRoute) => {
  if (!Array.isArray(CurrentUserRoute) || !CurrentUserRoute.length) return [];

  return SIDEMENU
    .map((menu) => {
      const filteredSubfields = menu.Subfields.filter((sub) => {
        const subRoute =
          sub.route ||
          (sub.name?.startsWith("/") ? sub.name : `/${sub.name}`);

        // Always show routes in the always-visible list
        if (ALWAYS_VISIBLE_ROUTES.includes(subRoute)) return true;

        return CurrentUserRoute.some(
          (userRoute) =>
            userRoute === subRoute ||
            userRoute.startsWith(subRoute + "/")
        );
      });

      if (!filteredSubfields.length) return null;

      return {
        ...menu,
        Subfields: filteredSubfields,
      };
    })
    .filter(Boolean);
};

const filteredSIDEMENU = filterSideMenuByRoutes(
  SIDEMENU,
  CurrentUserRoute
);
  const iconMap = {
    Employees: faUsers,
    Clients: faUserTie,
    Sales: faChartLine,
    Requests: faTasks,
    CROptions: faCog,
    Time_Records: faChartBar,
    Email: faEnvelope,
    Data_Bucket: faDatabase,
    Dashboard: faTachometerAlt,
    Bulk_Emails: faMailBulk,
    Finance: faWallet,
    Outlook: faEnvelope,
    Reports: faChartSimple,
    Generate_New_PaySlip: faFileInvoiceDollar,
    Dynamic_Route: faRoute,
    Admin_Issues: faExclamationTriangle,
    User_Issues: faExclamationTriangle,
    Email_Validation: faEnvelope,
    Ring_Central: faPhone,
    Hiring: faUserPlus,
  };

  function getIcon(name) {
    const val = unformatName(name);
    return iconMap[val] || faFolder;
  }

  const superAdminSidebarData = [
    {
      menu: filteredSIDEMENU.map((item) => ({
        menuValue: item.name,
        hasSubRoute: item.Subfields.length > 0,
        showSubRoute: false,
        icon: getIcon(item.name),
        subMenus: item.Subfields.map((sub) => {
          const subItem = {
            menuValue: sub.displayName,
            route: sub.name,
          };

          // 2. If this is the "My Handbooks" item, inject the API data
          if (sub.displayName === "My Handbooks") {
            subItem.subMenusValues = handbooks.map((hb) => ({
              menuValue: hb.name,
              route: `/my-handbooks/${hb._id}`,
            }));
          }

          // 3. If sub has static subMenusValues defined in databucket, use them
          if (sub.subMenusValues?.length) {
            subItem.subMenusValues = sub.subMenusValues.map((sv) => ({
              menuValue: sv.displayName,
              route: sv.name,
            }));
          }

          return subItem;
        }),
      })),
    },
  ];
  const [sidebarDataN, setSidebarDataN] = useState(superAdminSidebarData);
  const activeMenuValue = useSelector((state) => state?.task?.activeMenuValue)
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMouseOverSidebar, setMouseOverSidebar] = useState(false);

  // Sync sidebar data when access permissions load (or change).
  // useState only captures the initial value — without this effect the sidebar
  // stays blank if currentEmployeeAccess arrives after the first render.
  useEffect(() => {
    setSidebarDataN(superAdminSidebarData);
  }, [CurrentUserAccess, handbooks]); // eslint-disable-line

  useEffect(() => {
    if (userId) dispatch(fetchHandbooksForEmployee(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (
      isMouseOverSidebar &&
      document.body.classList.contains("mini-sidebar")
    ) {
      document.body.classList.add("expand-menu");
      return;
    }
    document.body.classList.remove("expand-menu");
  }, [isMouseOverSidebar]);

  useEffect(() => {
    if (pathname === "/employees") {
      dispatch(setActiveMenuValue("Employees"));
    } else {
      dispatch(setActiveMenuValue(pathname)); // fallback to route name
    }
  }, [pathname]);

  const handleMouseEnter = () => {
    document.body.classList.remove("mini-sidebar")
    setMouseOverSidebar(true);
  };

  const handleMouseLeave = () => {
    document.body.classList.add("mini-sidebar")
    setMouseOverSidebar(false);
  };
  const { t } = useTranslation();

  const expandSubMenus = (menu) => {
    // If the clicked menu is already the active one, toggle its submenu visibility
    if (activeMenu === menu?.menuValue) {
      setOpenSubMenu((prev) => (prev === menu?.menuValue ? null : menu?.menuValue));
    } else {
      setActiveMenu(menu?.menuValue);
      setOpenSubMenu(menu?.menuValue);
    }
    const updatedAdminSidebar = sidebarDataN.map((section) => {
      const updatedSection = { ...section };
      updatedSection.menu = section.menu?.map((menuItem) => {
        if (menu?.menuValue === menuItem.menuValue) {
          return {
            ...menuItem,
            showSubRoute: !menuItem.showSubRoute,
          };
        } else {
          return {
            ...menuItem,
            showSubRoute: false,
          };
        }
      });
      return updatedSection;
    });

    setSidebarDataN(superAdminSidebarData);
  };

  const handleMenuClick = (menu) => {
    document.body.classList.add("mini-sidebar");

    if (!menu.hasSubRoute) {
      dispatch(openTab({ path: menu.route, title: menu.menuValue, deduplicate: true }));
      dispatch(setActiveMenuValue(menu.menuValue));
      setActiveMenu(menu.menuValue);

      setOpenSubMenu(null);
    }
  };

  const handleSubMenuClick = (menu, subMenu = null) => {
    // 1. Check if this is a "Folder" (like My Handbooks)
    const isFolder = subMenu && subMenu.subMenusValues && subMenu.subMenusValues.length > 0;

    if (isFolder) {
      // TOGGLE the inner menu state without closing the sidebar
      setOpenInnerSubMenu((prev) => (prev === subMenu.menuValue ? null : subMenu.menuValue));
      // RETURN EARLY: Do not add mini-sidebar class, do not navigate
      return;
    }

    // 2. If it's a normal link, proceed with closing/navigating
    document.body.classList.add("mini-sidebar");

    const route = subMenu?.route || menu.route;
    dispatch(openTab({ path: route, title: subMenu?.menuValue || menu.menuValue, deduplicate: true }));
    dispatch(setActiveMenuValue(menu.menuValue));
    setActiveMenu(menu.menuValue);
  };

  const isActive = (menuValue, route, subMenus = []) => {
    if (activeMenuValue === menuValue || pathname === route || pathname.startsWith(route)) {
      return true;
    }

    for (const sub of subMenus) {
      if (
        pathname === sub.route ||
        pathname.startsWith(sub.route) ||
        (sub.subMenusValues &&
          sub.subMenusValues.some((nested) => pathname === nested.route || pathname.startsWith(nested.route)))
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <div
      className={`sidebar ${isSidebarVisible ? "show" : "hidden"}`}
      id="sidebar"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="sidebar-inner slimscroll" style={{ overflow: false }}>
        <div id="sidebar-menu" className="sidebar-menu">
          <Scrollbars
            autoHide={false}
            autoHideTimeout={1000}
            autoHideDuration={200}
            autoHeight
            autoHeightMin={0}
            autoHeightMax="88vh"
            thumbMinSize={30}
            universal={false}
            hideTracksWhenNotNeeded={true}
          >
            <ul className="sidebar-vertical" id="veritical-sidebar">
              {sidebarDataN?.map((mainTittle, index) => (
                <React.Fragment key={index + 1}>
                  {mainTittle?.menu?.map((menu, menuIndex) => (
                    <React.Fragment key={menuIndex + 1}>
                      {menu.hasSubRoute === false ? (
                        <li className={`${isActive(menu.menuValue, menu.route) ? "submenu" : ""}`}>
                          <Link to={menu.route} onClick={() => handleMenuClick(menu)}>
                            <FontAwesomeIcon
                              icon={menu?.icon}
                              className={`${isActive(menu.menuValue, menu.route) ? "active-icon" : ""}`}
                            />
                            <span className={isActive(menu.menuValue, menu.route) ? "active-icon" : ""}>
                              {menu.menuValue}
                            </span>
                            <span className={isActive(menu.menuValue, menu.route) ? "noti-dot" : ""}></span>
                          </Link>
                        </li>
                      ) : (
                        <li className={`submenu ${openSubMenu === menu.menuValue ? "subdrop" : ""}`}>
                          <Link to="#" onClick={() => expandSubMenus(menu)}>
                            <FontAwesomeIcon
                              icon={menu?.icon}
                              className={`${isActive(menu.menuValue, menu.route) ? "active-icon" : ""}`}
                            />
                            <span className={isActive(menu.menuValue, menu.route) ? "active-icon" : ""}>
                              {menu.menuValue}
                            </span>
                            {isActive(menu.menuValue, menu.route, menu.subMenus) && <span className="noti-dot"></span>}
                            <span className="menu-arrow"></span>
                          </Link>
                          <ul style={{ display: openSubMenu === menu.menuValue ? "block" : "none" }}>
                            {menu.subMenus?.map((subMenu, subMenuIndex) => (
                              <li key={subMenuIndex + 1}>
                                <Link
                                  // If it has handbooks, use "#" to stay on the page; otherwise use the route
                                  to={subMenu.subMenusValues ? "#" : subMenu.route}
                                  onClick={(e) => {
                                    if (subMenu.subMenusValues) {
                                      e.preventDefault(); // Stop the redirect to <MyHandbooks />
                                    }
                                    handleSubMenuClick(menu, subMenu);
                                  }}
                                  className={pathname.startsWith(subMenu.route) ? "active" : ""}
                                >
                                  {pathname.startsWith(subMenu.route) && subMenu.icon && (
                                    <FontAwesomeIcon icon={subMenu.icon} />
                                  )}
                                  <span>{subMenu.menuValue}</span>
                                  {subMenu.subMenusValues && <span className="menu-arrow"></span>}
                                </Link>
                                {subMenu.subMenusValues && (
                                  <ul style={{
                                    // Show if the URL is active OR if the user clicked it to toggle
                                    display: (pathname.includes(subMenu.route) || openInnerSubMenu === subMenu.menuValue) ? "block" : "none",
                                    paddingLeft: "25px", // Indent it further to look like a sub-level
                                    listStyleType: "none"
                                  }}>
                                    {subMenu.subMenusValues.map((value, index) => (
                                      <li key={index}>
                                        <Link
                                          to={value.route}
                                          onClick={() => handleSubMenuClick(menu, value)}
                                          className={pathname.startsWith(value.route) ? "active" : ""}
                                        >
                                          <span>{value.menuValue}</span>
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          </Scrollbars>
        </div>
      </div>

    </div>
  );
};

export default Sidebar;
